import { useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Alert, Button, ButtonLink, Field, Input } from '../../components/ui';
import { ApiError, api } from '../../lib/api';
import { usePageTitle } from '../../lib/usePageTitle';
import { AuthShell, fieldErrors } from './AuthShell';

export function ResetPassword() {
  usePageTitle('Choose a new password — WaveQA');

  const [params] = useSearchParams();
  const token = params.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (!token) return;

    setBusy(true);
    setError(null);

    try {
      await api.resetPassword({ token, password, confirm_password: confirm });
      setDone(true);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError('Something went wrong.', 0));
    } finally {
      setBusy(false);
    }
  }

  if (!token) {
    return (
      <AuthShell title="Reset link incomplete">
        <Alert tone="error">
          This link is missing its token. Open the link from your email directly
          rather than copying part of it.
        </Alert>
        <ButtonLink to="/forgot-password" variant="secondary" className="w-full">
          Request a new link
        </ButtonLink>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password changed">
        <Alert tone="success">
          Your password has been changed. Every existing session was signed out.
        </Alert>
        <p className="mb-5 text-sm text-hull-300">
          Signing out everywhere is deliberate: a reset is how you recover an
          account, and leaving old sessions alive would let whoever prompted the
          reset keep their access.
        </p>
        <ButtonLink to="/login" className="w-full">
          Sign in
        </ButtonLink>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Choose a new password"
      subtitle="This link works once. Everything currently signed in will be signed out."
      footer={
        <Link to="/login" className="text-surge-400 hover:text-surge-300">
          Back to sign in
        </Link>
      }
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && <Alert tone="error">{error.message}</Alert>}

        <Field
          label="New password"
          htmlFor="password"
          hint="At least 12 characters, with upper and lower case, a digit and a symbol."
          errors={fieldErrors(error?.fields, 'password')}
        >
          <Input
            id="password"
            type="password"
            autoComplete="new-password"
            required
            autoFocus
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            invalid={Boolean(fieldErrors(error?.fields, 'password'))}
          />
        </Field>

        <Field
          label="Confirm new password"
          htmlFor="confirm"
          errors={fieldErrors(error?.fields, 'confirm_password')}
        >
          <Input
            id="confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            invalid={Boolean(fieldErrors(error?.fields, 'confirm_password'))}
          />
        </Field>

        <Button type="submit" disabled={busy} className="w-full">
          {busy ? 'Saving…' : 'Change password'}
        </Button>
      </form>
    </AuthShell>
  );
}
